import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import type { Footer as FooterSchema } from '@amplience/content-types/typings/c-footer';
import type { MasterLayoutContextType } from '../../context';
import { useLocaleContext } from '../../context';
import { isBusinessComponent, renderAmplienceSlotComponents } from '../../utils/amplienceUtils';
import { createGlobalAmplienceComponent } from '../globalAmplienceComponents.utils';
import { HandlebarTemplate, type HandlebarTemplateProps } from '../handlebarTemplate/HandlebarTemplate';
import { BottomContent, ColumnContent, ColumnWrapper, Content } from './Footer.styles';
import { NewsletterSignUp } from './newsletterSignUp/NewsletterSignUp';
import { ScrollToTop } from './scrollToTop/ScrollToTop';

export type FooterComponentProps = FooterSchema & {
  handlebarComponents: HandlebarTemplateProps[] | null;
  focusReset?: () => void;
};

export function FooterComponent({
  footerColumn,
  scrollToTop,
  newsletterSignUp,
  footerBottom,
  businessSlot,
  handlebarComponents,
  focusReset,
}: FooterComponentProps) {
  const { query } = useRouter();
  const { locale } = useLocaleContext();
  const isCategoryPage = useMemo(() => query.slug?.at(-1)?.toLocaleLowerCase().endsWith('-c') ?? false, [query.slug]);

  const renderBusinessSlot = () => {
    const isBusinessComponentSlot = businessSlot ? isBusinessComponent(businessSlot) : false;

    return (
      isBusinessComponentSlot && (
        <ColumnWrapper data-testid="footer-business-slot">
          {renderAmplienceSlotComponents(
            [businessSlot],
            { 'handlebar-component': HandlebarTemplate },
            handlebarComponents,
            locale,
          )}
        </ColumnWrapper>
      )
    );
  };

  if (!footerColumn && !scrollToTop && !newsletterSignUp && !footerBottom) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let footerColumnAmplienceComponents: any = {};
  const footerBottomAmplienceComponents = {
    ...createGlobalAmplienceComponent(footerBottom?.bottomContent ?? []),
  };

  footerColumn?.forEach((column) => {
    footerColumnAmplienceComponents = {
      ...footerColumnAmplienceComponents,
      ...createGlobalAmplienceComponent(column.columnContent ?? []),
    };
  });

  return (
    <footer role="contentinfo">
      {scrollToTop && <ScrollToTop {...scrollToTop} isCategoryPage={isCategoryPage} focusReset={focusReset} />}
      {newsletterSignUp && <NewsletterSignUp {...newsletterSignUp} />}
      <ColumnWrapper data-testid="columnSection">
        <ColumnContent data-testid="columnContent">
          {footerColumn?.map((column, i) => (
            <Content key={i} data-testid={`footer-column-content-${i + 1}`}>
              {column.columnContent?.map((columnItem, j) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const Component: (props: any) => ReactElement | null =
                  footerColumnAmplienceComponents[columnItem._meta.schema];

                return Component && <Component {...columnItem} key={`${columnItem._meta.schema}-${j}`} />;
              })}
            </Content>
          ))}
        </ColumnContent>
      </ColumnWrapper>

      {renderBusinessSlot()}

      <ColumnWrapper>
        <BottomContent $numOfItems={footerBottom?.bottomContent?.length} data-testid="footer-bottom-content">
          {footerBottom?.bottomContent?.map((bottomItem, k) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const Component: (props: any) => ReactElement | null =
              footerBottomAmplienceComponents[bottomItem._meta.schema];

            return (
              Component && (
                <Component
                  {...bottomItem}
                  key={`${bottomItem._meta.schema}-${k}`}
                  data-testid={`footer-bottom-content-${k + 1}`}
                />
              )
            );
          })}
        </BottomContent>
      </ColumnWrapper>
    </footer>
  );
}

export type FooterProps = {
  footerData?: MasterLayoutContextType['masterLayoutData']['footerComponent'];
  handlebarComponents: HandlebarTemplateProps[] | null;
  focusReset?: () => void;
};

export function Footer({ footerData, handlebarComponents, focusReset }: FooterProps) {
  if (!footerData) return null;

  return <FooterComponent {...footerData} handlebarComponents={handlebarComponents} focusReset={focusReset} />;
}

export default Footer;
